import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { TemplateObjectType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import makeArray from 'webapp/helpers/make-array';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import MetricLibraryGridComponent from 'webapp/components/metric-library/grid/index';
import type { Args as GridBaseArgs } from 'webapp/components/metric-library/grid/index';
import TemplatesGridItemComponent from 'webapp/components/templates/grid-item';

import type FilterSortMetricLibraryMainTemplates from 'webapp/models-custom/filter-sort/metric-library-main-templates';
import type FilterSortMetricLibrarySourceTemplates from 'webapp/models-custom/filter-sort/metric-library-source-templates';
import type Template from 'webapp/models-custom/template';

interface Args extends Omit<GridBaseArgs, 'filterSort'> {
	filterSort: FilterSortMetricLibrarySourceTemplates | FilterSortMetricLibraryMainTemplates;
}

export default class MetricLibraryGridTemplatesComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	visibleCheckContainerNode: HTMLElement | null = null;

	@action
	previewTemplate(model: Template): void {
		this.mmTemplatesService.openPreviewPopup(
			model,
			() => {
				this.useTemplate(model);
			},
			'library',
		);
	}

	@action
	useTemplate(model: Template): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.mmTemplatesService.trackEvent(model, TrackingEvent.USE_A_TEMPLATE);

		if (model.objectType === TemplateObjectType.BOARD) {
			this.mmBoardsService.createBoardFromTemplate(model, 'public_template', (board) => {
				this.mmBoardsService.afterCreateBoardFromTemplate(board);
			});
		} else {
			this.mmReportsService.createReportFromTemplate(
				model,
				'report_public_template',
				this.mmReportsService.afterCreateReportFromTemplate,
			);
		}
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	setupView(node: HTMLElement): void {
		this.visibleCheckContainerNode = node;
	}
	<template>
		<MetricLibraryGridComponent
			@filterSort={{@filterSort}}
			@title={{@title}}
			@initialRowCount={{@initialRowCount}}
			@classNames={{@classNames}}
			@animationProperty="id"
			as |visibleItems settings|
		>
			<ul
				class="flex-ct flex-ct--size-board"
				data-hidden={{if visibleItems "false" "true"}}
				{{didInsert this.setupView}}
				{{visibleCheckContainer}}
				{{didUpdate this.triggerContentChange visibleItems}}
			>
				{{#each visibleItems as |model|}}
					<TemplatesGridItemComponent
						@model={{model}}
						@onPreviewTemplate={{fn this.previewTemplate model}}
						@onUseTemplate={{fn this.useTemplate model}}
					/>
				{{/each}}

				{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
				{{#each (makeArray length=settings.itemsPerRow)}}
					<li class="flex-item flex-item--fake"></li>
				{{/each}}
			</ul>
		</MetricLibraryGridComponent>
	</template>
}
